export const locales = ['en', 'de', 'fr', 'es', 'it', 'nl'] as const;

export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = 'en';

export const localeMeta: Record<Locale, { flag: string; label: string; nativeLabel: string }> = {
  en: { flag: '🇺🇸', label: 'English', nativeLabel: 'English (US)' },
  de: { flag: '🇩🇪', label: 'German', nativeLabel: 'Deutsch' },
  fr: { flag: '🇫🇷', label: 'French', nativeLabel: 'Français' },
  es: { flag: '🇪🇸', label: 'Spanish', nativeLabel: 'Español' },
  it: { flag: '🇮🇹', label: 'Italian', nativeLabel: 'Italiano' },
  nl: { flag: '🇳🇱', label: 'Dutch', nativeLabel: 'Nederlands' },
};

const en = {
  'common.language': 'Language',
  'common.dashboard': 'Dashboard',
  'common.apiKeys': 'API Keys',
  'common.playground': 'Playground',
  'common.team': 'Team',
  'common.auditLog': 'Audit Log',
  'common.webhooks': 'Webhooks',
  'common.security': 'Security',
  'common.docs': 'Docs',
  'common.billing': 'Billing',
  'common.upgrade': 'Upgrade',
  'common.signOut': 'Sign out',
  'common.cancel': 'Cancel',
  'common.downloadHtml': 'Download HTML',
  'common.downloadCsv': 'Download CSV',
  'common.viewPlans': 'View Plans',
  'common.copy': 'Copy',
  'common.copyUrl': 'Copy URL',
  'common.copyResponse': 'Copy Response',
  'common.copied': 'Copied!',

  'header.envLocal': 'Local',
  'header.envStaging': 'Staging',
  'header.envProduction': 'Production',
  'header.staff': 'Staff',
  'header.adminTools': 'Admin Tools',
  'header.superAdmin': 'Super Admin',
  'header.blogManager': 'Blog Manager',
  'header.ownerTools': 'Owner Tools',
  'header.notifications': 'Notifications',
  'header.signedInAs': 'Signed in as',
  'header.toggleMenu': 'Toggle menu',
  'header.closeMenu': 'Close menu',
  'header.logout': 'Logout',

  'layout.quotaBanner': 'You are at {percent}% of your monthly quota ({used}/{limit}). Consider upgrading to avoid interruptions.',

  'playground.title': 'Verification Playground',
  'playground.step.configure': 'Configure',
  'playground.step.scanQr': 'Scan QR',
  'playground.step.results': 'Results',
  'playground.liveSignalTag': 'Live Sandbox Signal',
  'playground.realWorldMode': 'Real-world simulation mode',
  'playground.liveSignal.ready': 'Ready to run a realistic verification simulation',
  'playground.liveSignal.pending': 'Waiting for wallet scan and callback',
  'playground.liveSignal.inProgress': 'Proofs received, policy checks in progress',
  'playground.liveSignal.completed': 'Verification accepted and signed',
  'playground.liveSignal.failed': 'Verification rejected by policy rules',
  'playground.liveSignal.expired': 'Session expired before final proof submission',
  'playground.liveSignal.description': 'Simulates the same sequence teams run in production: wallet handoff, credential proofing, then policy decisioning.',
  'playground.executionComplete': '{value}% execution complete',
  'playground.trustSignal': 'Trust Signal',
  'playground.confidenceScore': 'Confidence score',
  'playground.highConfidence': 'High confidence',
  'playground.strongConfidence': 'Strong confidence',
  'playground.moderateConfidence': 'Moderate confidence',
  'playground.lowConfidence': 'Low confidence',
  'playground.passRate': 'Pass rate',
  'playground.riskScore': 'Risk score',
  'playground.businessUnlock': 'Richer scenarios and country-grade profiles are unlocked on higher plans.',
  'playground.trial3dHint': 'Interactive 3D demo is available on Pro and Business plans.',
  'playground.pro3dHint': 'Pro includes 3D interaction. Business adds cinematic motion and country profile simulations.',
  'playground.business3dHint': 'Cinematic 3D mode is active for this sandbox.',
  'playground.threeDProPlus': '3D on Pro+',

  'playground.milestone.session': 'Session issued',
  'playground.milestone.sessionDetail': 'Challenge generated and signed by WalletGate',
  'playground.milestone.handoff': 'Wallet handoff',
  'playground.milestone.handoffPending': 'Awaiting wallet response ({timeLeft} left)',
  'playground.milestone.handoffDone': 'Wallet callback captured',
  'playground.milestone.proof': 'Credential proofing',
  'playground.milestone.proofDetail': '{count} policy checks evaluated',
  'playground.milestone.policy': 'Policy decision',
  'playground.milestone.policyPending': 'Risk engine preparing decision',
  'playground.milestone.policyDone': 'Final state: {status}',

  'playground.configuration': 'Configuration',
  'playground.environment': 'Environment',
  'playground.test': 'Test',
  'playground.live': 'Live',
  'playground.liveQuotaHint': 'Live verifications count against your monthly quota.',
  'playground.testModeHint': 'Test mode: Safe sandbox verification',
  'playground.liveModeHint': 'Live mode: Real verifications count toward quota',
  'playground.apiKey': 'API Key',
  'playground.createKeyCta': 'Create a {environment} key to continue →',
  'playground.unnamedKey': 'Unnamed Key',
  'playground.verificationChecks': 'Verification Checks',
  'playground.addCheck': '+ Add check',
  'playground.check.ageOver': 'Age Over',
  'playground.check.euResidency': 'EU Residency',
  'playground.check.identityVerified': 'Identity Verified',
  'playground.startVerification': 'Start Verification',
  'playground.creating': 'Creating...',
  'playground.newSession': 'New Session',
  'playground.createSessionFailed': 'Failed to create session',

  'playground.readyToTest': 'Ready to Test',
  'playground.readyToTestHint': 'Configure checks and start a verification session',
  'playground.waitingForQr': 'Waiting for QR code...',

  'playground.verificationSession': 'Verification Session',
  'playground.status.pending': 'pending',
  'playground.status.inProgress': 'in progress',
  'playground.status.completed': 'completed',
  'playground.status.failed': 'failed',
  'playground.status.expired': 'expired',
  'playground.scanToVerify': 'Scan to verify',
  'playground.scanWithWallet': 'Scan with EUDI Wallet app',
  'playground.expiresIn': 'Expires in {time}',
  'playground.sessionId': 'Session ID',
  'playground.deepLink': 'Deep Link',
  'playground.verified': 'Verified',
  'playground.failed': 'Failed',
  'playground.expired': 'Expired',
  'playground.checkResults': 'Check Results',
  'playground.passed': 'Passed',

  'playground.quickSimulate': 'Quick Simulate',
  'playground.testOnly': 'Test Only',
  'playground.simulateHint': 'Skip the wallet scan and simulate a response instantly.',
  'playground.sim.passAll': 'Pass All Checks',
  'playground.sim.failAll': 'Fail All Checks',
  'playground.sim.custom': 'Custom Results',
  'playground.sim.scenario': 'Pre-built Scenarios',
  'playground.sim.scenarioLabel': 'Scenario',
  'playground.sim.loadingScenarios': 'Loading scenarios...',
  'playground.sim.prebuiltRequiresPro': 'Pre-built test scenarios are available on Pro and above.',
  'playground.sim.countryRequiresBusiness': 'Country profile scenarios require the Business plan.',
  'playground.sim.customRequiresPaid': 'Custom results require a paid plan.',
  'playground.submitSimulation': 'Submit Simulation',
  'playground.simulating': 'Simulating...',
  'playground.simulationFailed': 'Simulation failed',
  'playground.proLimitReached': 'Pro sandbox run limit reached for the last 30 days. Upgrade to Business for unlimited runs.',
  'playground.upgrade': 'Upgrade',

  'playground.scenarioBuilder': 'Scenario Builder',
  'playground.businessPlan': 'Business Plan',
  'playground.scenarioBuilderHint': 'Create reusable simulation recipes to test different wallet outcomes.',
  'playground.customScenarioBusinessOnly': 'Custom scenarios are available on the Business plan.',
  'playground.complianceReport': 'Compliance Report',
  'playground.complianceReportHint': 'Export a readiness report for audits and stakeholder reviews.',
  'playground.complianceRequiresBusiness': 'Compliance reports require the Business plan.',
  'playground.last7Days': 'Last 7 days',
  'playground.last30Days': 'Last 30 days',
  'playground.last90Days': 'Last 90 days',
  'playground.runHistory': 'Run History',
  'playground.noRuns': 'No sandbox runs yet. Simulate a session to see history here.',
  'playground.rawJson': 'Raw JSON',
  'playground.request': 'Request',
  'playground.response': 'Response',
  'playground.integrationCode': 'Integration Code',
} as const;

export type TranslationKey = keyof typeof en;

type TranslationDict = Record<TranslationKey, string>;

const de: TranslationDict = {
  'common.language': 'Sprache',
  'common.dashboard': 'Dashboard',
  'common.apiKeys': 'API-Schlüssel',
  'common.playground': 'Playground',
  'common.team': 'Team',
  'common.auditLog': 'Audit-Log',
  'common.webhooks': 'Webhooks',
  'common.security': 'Sicherheit',
  'common.docs': 'Dokumentation',
  'common.billing': 'Abrechnung',
  'common.upgrade': 'Upgrade',
  'common.signOut': 'Abmelden',
  'common.cancel': 'Abbrechen',
  'common.downloadHtml': 'HTML herunterladen',
  'common.downloadCsv': 'CSV herunterladen',
  'common.viewPlans': 'Tarife ansehen',
  'common.copy': 'Kopieren',
  'common.copyUrl': 'URL kopieren',
  'common.copyResponse': 'Antwort kopieren',
  'common.copied': 'Kopiert!',

  'header.envLocal': 'Lokal',
  'header.envStaging': 'Staging',
  'header.envProduction': 'Produktion',
  'header.staff': 'Mitarbeiter',
  'header.adminTools': 'Admin-Tools',
  'header.superAdmin': 'Super-Admin',
  'header.blogManager': 'Blog-Manager',
  'header.ownerTools': 'Inhaber-Tools',
  'header.notifications': 'Benachrichtigungen',
  'header.signedInAs': 'Angemeldet als',
  'header.toggleMenu': 'Menü umschalten',
  'header.closeMenu': 'Menü schließen',
  'header.logout': 'Abmelden',

  'layout.quotaBanner': 'Sie haben {percent}% Ihres monatlichen Kontingents erreicht ({used}/{limit}). Erwägen Sie ein Upgrade, um Unterbrechungen zu vermeiden.',

  'playground.title': 'Verifizierungs-Playground',
  'playground.step.configure': 'Konfigurieren',
  'playground.step.scanQr': 'QR scannen',
  'playground.step.results': 'Ergebnisse',
  'playground.liveSignalTag': 'Live-Sandbox-Signal',
  'playground.realWorldMode': 'Simulation im Realbetrieb',
  'playground.liveSignal.ready': 'Bereit für eine realistische Verifizierungssimulation',
  'playground.liveSignal.pending': 'Warten auf Wallet-Scan und Callback',
  'playground.liveSignal.inProgress': 'Nachweise eingegangen, Richtlinienprüfung läuft',
  'playground.liveSignal.completed': 'Verifizierung akzeptiert und signiert',
  'playground.liveSignal.failed': 'Verifizierung durch Richtlinien abgelehnt',
  'playground.liveSignal.expired': 'Sitzung vor endgültiger Nachweisübermittlung abgelaufen',
  'playground.liveSignal.description': 'Simuliert denselben Ablauf wie im Produktivbetrieb: Wallet-Übergabe, Credential-Prüfung und Richtlinienentscheidung.',
  'playground.executionComplete': '{value}% Ausführung abgeschlossen',
  'playground.trustSignal': 'Vertrauenssignal',
  'playground.confidenceScore': 'Vertrauenswert',
  'playground.highConfidence': 'Hohe Sicherheit',
  'playground.strongConfidence': 'Starke Sicherheit',
  'playground.moderateConfidence': 'Mittlere Sicherheit',
  'playground.lowConfidence': 'Niedrige Sicherheit',
  'playground.passRate': 'Bestehensquote',
  'playground.riskScore': 'Risikowert',
  'playground.businessUnlock': 'Erweiterte Szenarien und Ländermodelle sind in höheren Tarifen verfügbar.',
  'playground.trial3dHint': 'Die interaktive 3D-Demo ist in Pro- und Business-Tarifen verfügbar.',
  'playground.pro3dHint': 'Pro enthält 3D-Interaktion. Business ergänzt cineastische Animationen und Länderprofile.',
  'playground.business3dHint': 'Cineastischer 3D-Modus ist für diese Sandbox aktiv.',
  'playground.threeDProPlus': '3D ab Pro',

  'playground.milestone.session': 'Sitzung erstellt',
  'playground.milestone.sessionDetail': 'Challenge von WalletGate erzeugt und signiert',
  'playground.milestone.handoff': 'Wallet-Übergabe',
  'playground.milestone.handoffPending': 'Warte auf Wallet-Antwort ({timeLeft} verbleibend)',
  'playground.milestone.handoffDone': 'Wallet-Callback erfasst',
  'playground.milestone.proof': 'Credential-Prüfung',
  'playground.milestone.proofDetail': '{count} Richtlinienprüfungen ausgewertet',
  'playground.milestone.policy': 'Richtlinienentscheidung',
  'playground.milestone.policyPending': 'Risiko-Engine bereitet Entscheidung vor',
  'playground.milestone.policyDone': 'Endstatus: {status}',

  'playground.configuration': 'Konfiguration',
  'playground.environment': 'Umgebung',
  'playground.test': 'Test',
  'playground.live': 'Live',
  'playground.liveQuotaHint': 'Live-Verifizierungen werden auf Ihr monatliches Kontingent angerechnet.',
  'playground.testModeHint': 'Testmodus: Sichere Sandbox-Verifizierung',
  'playground.liveModeHint': 'Live-Modus: Echte Verifizierungen zählen zum Kontingent',
  'playground.apiKey': 'API-Schlüssel',
  'playground.createKeyCta': 'Erstellen Sie einen {environment}-Schlüssel, um fortzufahren →',
  'playground.unnamedKey': 'Unbenannter Schlüssel',
  'playground.verificationChecks': 'Verifizierungsprüfungen',
  'playground.addCheck': '+ Prüfung hinzufügen',
  'playground.check.ageOver': 'Mindestalter',
  'playground.check.euResidency': 'EU-Wohnsitz',
  'playground.check.identityVerified': 'Identität verifiziert',
  'playground.startVerification': 'Verifizierung starten',
  'playground.creating': 'Wird erstellt...',
  'playground.newSession': 'Neue Sitzung',
  'playground.createSessionFailed': 'Sitzung konnte nicht erstellt werden',

  'playground.readyToTest': 'Testbereit',
  'playground.readyToTestHint': 'Konfigurieren Sie Prüfungen und starten Sie eine Verifizierungssitzung',
  'playground.waitingForQr': 'Warte auf QR-Code...',

  'playground.verificationSession': 'Verifizierungssitzung',
  'playground.status.pending': 'ausstehend',
  'playground.status.inProgress': 'in bearbeitung',
  'playground.status.completed': 'abgeschlossen',
  'playground.status.failed': 'fehlgeschlagen',
  'playground.status.expired': 'abgelaufen',
  'playground.scanToVerify': 'Zum Verifizieren scannen',
  'playground.scanWithWallet': 'Mit der EUDI-Wallet-App scannen',
  'playground.expiresIn': 'Läuft ab in {time}',
  'playground.sessionId': 'Sitzungs-ID',
  'playground.deepLink': 'Deep Link',
  'playground.verified': 'Verifiziert',
  'playground.failed': 'Fehlgeschlagen',
  'playground.expired': 'Abgelaufen',
  'playground.checkResults': 'Prüfergebnisse',
  'playground.passed': 'Bestanden',

  'playground.quickSimulate': 'Schnellsimulation',
  'playground.testOnly': 'Nur Test',
  'playground.simulateHint': 'Überspringen Sie den Wallet-Scan und simulieren Sie sofort eine Antwort.',
  'playground.sim.passAll': 'Alle Prüfungen bestehen',
  'playground.sim.failAll': 'Alle Prüfungen fehlschlagen',
  'playground.sim.custom': 'Benutzerdefinierte Ergebnisse',
  'playground.sim.scenario': 'Vordefinierte Szenarien',
  'playground.sim.scenarioLabel': 'Szenario',
  'playground.sim.loadingScenarios': 'Szenarien werden geladen...',
  'playground.sim.prebuiltRequiresPro': 'Vordefinierte Testszenarien sind ab Pro verfügbar.',
  'playground.sim.countryRequiresBusiness': 'Länderprofil-Szenarien erfordern den Business-Tarif.',
  'playground.sim.customRequiresPaid': 'Benutzerdefinierte Ergebnisse erfordern einen kostenpflichtigen Tarif.',
  'playground.submitSimulation': 'Simulation ausführen',
  'playground.simulating': 'Simulation läuft...',
  'playground.simulationFailed': 'Simulation fehlgeschlagen',
  'playground.proLimitReached': 'Das Pro-Sandbox-Limit für die letzten 30 Tage ist erreicht. Upgrade auf Business für unbegrenzte Runs.',
  'playground.upgrade': 'Upgrade',

  'playground.scenarioBuilder': 'Szenario-Builder',
  'playground.businessPlan': 'Business-Tarif',
  'playground.scenarioBuilderHint': 'Erstellen Sie wiederverwendbare Simulationen für unterschiedliche Wallet-Ergebnisse.',
  'playground.customScenarioBusinessOnly': 'Benutzerdefinierte Szenarien sind im Business-Tarif verfügbar.',
  'playground.complianceReport': 'Compliance-Bericht',
  'playground.complianceReportHint': 'Exportieren Sie einen Readiness-Bericht für Audits und Stakeholder.',
  'playground.complianceRequiresBusiness': 'Compliance-Berichte erfordern den Business-Tarif.',
  'playground.last7Days': 'Letzte 7 Tage',
  'playground.last30Days': 'Letzte 30 Tage',
  'playground.last90Days': 'Letzte 90 Tage',
  'playground.runHistory': 'Ausführungsverlauf',
  'playground.noRuns': 'Noch keine Sandbox-Runs. Simulieren Sie eine Sitzung, um den Verlauf zu sehen.',
  'playground.rawJson': 'Roh-JSON',
  'playground.request': 'Anfrage',
  'playground.response': 'Antwort',
  'playground.integrationCode': 'Integrationscode',
};

const fr: TranslationDict = {
  'common.language': 'Langue',
  'common.dashboard': 'Tableau de bord',
  'common.apiKeys': 'Clés API',
  'common.playground': 'Playground',
  'common.team': 'Équipe',
  'common.auditLog': 'Journal d’audit',
  'common.webhooks': 'Webhooks',
  'common.security': 'Sécurité',
  'common.docs': 'Documentation',
  'common.billing': 'Facturation',
  'common.upgrade': 'Mettre à niveau',
  'common.signOut': 'Se déconnecter',
  'common.cancel': 'Annuler',
  'common.downloadHtml': 'Télécharger HTML',
  'common.downloadCsv': 'Télécharger CSV',
  'common.viewPlans': 'Voir les offres',
  'common.copy': 'Copier',
  'common.copyUrl': 'Copier l’URL',
  'common.copyResponse': 'Copier la réponse',
  'common.copied': 'Copié !',

  'header.envLocal': 'Local',
  'header.envStaging': 'Préproduction',
  'header.envProduction': 'Production',
  'header.staff': 'Équipe',
  'header.adminTools': 'Outils admin',
  'header.superAdmin': 'Super Admin',
  'header.blogManager': 'Gestionnaire de blog',
  'header.ownerTools': 'Outils propriétaire',
  'header.notifications': 'Notifications',
  'header.signedInAs': 'Connecté en tant que',
  'header.toggleMenu': 'Ouvrir le menu',
  'header.closeMenu': 'Fermer le menu',
  'header.logout': 'Déconnexion',

  'layout.quotaBanner': 'Vous avez atteint {percent}% de votre quota mensuel ({used}/{limit}). Envisagez une mise à niveau pour éviter les interruptions.',

  'playground.title': 'Playground de vérification',
  'playground.step.configure': 'Configurer',
  'playground.step.scanQr': 'Scanner le QR',
  'playground.step.results': 'Résultats',
  'playground.liveSignalTag': 'Signal Sandbox en direct',
  'playground.realWorldMode': 'Mode simulation réelle',
  'playground.liveSignal.ready': 'Prêt à lancer une simulation de vérification réaliste',
  'playground.liveSignal.pending': 'En attente du scan wallet et du callback',
  'playground.liveSignal.inProgress': 'Preuves reçues, contrôles de politique en cours',
  'playground.liveSignal.completed': 'Vérification acceptée et signée',
  'playground.liveSignal.failed': 'Vérification rejetée par les règles',
  'playground.liveSignal.expired': 'Session expirée avant soumission finale des preuves',
  'playground.liveSignal.description': 'Simule la même séquence qu’en production : passage wallet, vérification des justificatifs, puis décision de politique.',
  'playground.executionComplete': '{value}% d’exécution terminée',
  'playground.trustSignal': 'Signal de confiance',
  'playground.confidenceScore': 'Score de confiance',
  'playground.highConfidence': 'Confiance élevée',
  'playground.strongConfidence': 'Confiance forte',
  'playground.moderateConfidence': 'Confiance modérée',
  'playground.lowConfidence': 'Confiance faible',
  'playground.passRate': 'Taux de réussite',
  'playground.riskScore': 'Score de risque',
  'playground.businessUnlock': 'Les scénarios avancés et profils pays sont disponibles dans les offres supérieures.',
  'playground.trial3dHint': 'La démo 3D interactive est disponible sur les offres Pro et Business.',
  'playground.pro3dHint': 'Pro inclut l’interaction 3D. Business ajoute les animations cinématiques et les profils pays.',
  'playground.business3dHint': 'Le mode 3D cinématique est actif pour cette sandbox.',
  'playground.threeDProPlus': '3D en Pro+',

  'playground.milestone.session': 'Session émise',
  'playground.milestone.sessionDetail': 'Challenge généré et signé par WalletGate',
  'playground.milestone.handoff': 'Passage wallet',
  'playground.milestone.handoffPending': 'En attente de la réponse wallet ({timeLeft} restantes)',
  'playground.milestone.handoffDone': 'Callback wallet reçu',
  'playground.milestone.proof': 'Contrôle des justificatifs',
  'playground.milestone.proofDetail': '{count} contrôles de politique évalués',
  'playground.milestone.policy': 'Décision de politique',
  'playground.milestone.policyPending': 'Le moteur de risque prépare la décision',
  'playground.milestone.policyDone': 'État final : {status}',

  'playground.configuration': 'Configuration',
  'playground.environment': 'Environnement',
  'playground.test': 'Test',
  'playground.live': 'Live',
  'playground.liveQuotaHint': 'Les vérifications Live sont déduites de votre quota mensuel.',
  'playground.testModeHint': 'Mode test : vérification sandbox sécurisée',
  'playground.liveModeHint': 'Mode live : les vérifications comptent dans le quota',
  'playground.apiKey': 'Clé API',
  'playground.createKeyCta': 'Créez une clé {environment} pour continuer →',
  'playground.unnamedKey': 'Clé sans nom',
  'playground.verificationChecks': 'Contrôles de vérification',
  'playground.addCheck': '+ Ajouter un contrôle',
  'playground.check.ageOver': 'Âge minimum',
  'playground.check.euResidency': 'Résidence UE',
  'playground.check.identityVerified': 'Identité vérifiée',
  'playground.startVerification': 'Démarrer la vérification',
  'playground.creating': 'Création...',
  'playground.newSession': 'Nouvelle session',
  'playground.createSessionFailed': 'Échec de création de session',

  'playground.readyToTest': 'Prêt pour le test',
  'playground.readyToTestHint': 'Configurez les contrôles et démarrez une session de vérification',
  'playground.waitingForQr': 'En attente du QR code...',

  'playground.verificationSession': 'Session de vérification',
  'playground.status.pending': 'en attente',
  'playground.status.inProgress': 'en cours',
  'playground.status.completed': 'terminé',
  'playground.status.failed': 'échec',
  'playground.status.expired': 'expiré',
  'playground.scanToVerify': 'Scannez pour vérifier',
  'playground.scanWithWallet': 'Scannez avec l’app Wallet EUDI',
  'playground.expiresIn': 'Expire dans {time}',
  'playground.sessionId': 'ID de session',
  'playground.deepLink': 'Lien profond',
  'playground.verified': 'Vérifié',
  'playground.failed': 'Échoué',
  'playground.expired': 'Expiré',
  'playground.checkResults': 'Résultats des contrôles',
  'playground.passed': 'Réussi',

  'playground.quickSimulate': 'Simulation rapide',
  'playground.testOnly': 'Test uniquement',
  'playground.simulateHint': 'Ignorez le scan wallet et simulez une réponse instantanément.',
  'playground.sim.passAll': 'Valider tous les contrôles',
  'playground.sim.failAll': 'Échouer tous les contrôles',
  'playground.sim.custom': 'Résultats personnalisés',
  'playground.sim.scenario': 'Scénarios préconfigurés',
  'playground.sim.scenarioLabel': 'Scénario',
  'playground.sim.loadingScenarios': 'Chargement des scénarios...',
  'playground.sim.prebuiltRequiresPro': 'Les scénarios de test préconfigurés sont disponibles à partir de Pro.',
  'playground.sim.countryRequiresBusiness': 'Les scénarios de profil pays nécessitent l’offre Business.',
  'playground.sim.customRequiresPaid': 'Les résultats personnalisés nécessitent une offre payante.',
  'playground.submitSimulation': 'Lancer la simulation',
  'playground.simulating': 'Simulation...',
  'playground.simulationFailed': 'Échec de la simulation',
  'playground.proLimitReached': 'La limite Pro de sandbox sur 30 jours est atteinte. Passez à Business pour des exécutions illimitées.',
  'playground.upgrade': 'Mettre à niveau',

  'playground.scenarioBuilder': 'Générateur de scénarios',
  'playground.businessPlan': 'Offre Business',
  'playground.scenarioBuilderHint': 'Créez des recettes de simulation réutilisables pour différents résultats wallet.',
  'playground.customScenarioBusinessOnly': 'Les scénarios personnalisés sont disponibles sur l’offre Business.',
  'playground.complianceReport': 'Rapport de conformité',
  'playground.complianceReportHint': 'Exportez un rapport de préparation pour les audits et parties prenantes.',
  'playground.complianceRequiresBusiness': 'Les rapports de conformité nécessitent l’offre Business.',
  'playground.last7Days': '7 derniers jours',
  'playground.last30Days': '30 derniers jours',
  'playground.last90Days': '90 derniers jours',
  'playground.runHistory': 'Historique des exécutions',
  'playground.noRuns': 'Aucune exécution sandbox. Simulez une session pour voir l’historique ici.',
  'playground.rawJson': 'JSON brut',
  'playground.request': 'Requête',
  'playground.response': 'Réponse',
  'playground.integrationCode': 'Code d’intégration',
};

const es: TranslationDict = {
  'common.language': 'Idioma',
  'common.dashboard': 'Panel',
  'common.apiKeys': 'Claves API',
  'common.playground': 'Playground',
  'common.team': 'Equipo',
  'common.auditLog': 'Registro de auditoría',
  'common.webhooks': 'Webhooks',
  'common.security': 'Seguridad',
  'common.docs': 'Documentación',
  'common.billing': 'Facturación',
  'common.upgrade': 'Actualizar plan',
  'common.signOut': 'Cerrar sesión',
  'common.cancel': 'Cancelar',
  'common.downloadHtml': 'Descargar HTML',
  'common.downloadCsv': 'Descargar CSV',
  'common.viewPlans': 'Ver planes',
  'common.copy': 'Copiar',
  'common.copyUrl': 'Copiar URL',
  'common.copyResponse': 'Copiar respuesta',
  'common.copied': '¡Copiado!',

  'header.envLocal': 'Local',
  'header.envStaging': 'Preproducción',
  'header.envProduction': 'Producción',
  'header.staff': 'Equipo',
  'header.adminTools': 'Herramientas admin',
  'header.superAdmin': 'Super Admin',
  'header.blogManager': 'Gestor del blog',
  'header.ownerTools': 'Herramientas del propietario',
  'header.notifications': 'Notificaciones',
  'header.signedInAs': 'Sesión iniciada como',
  'header.toggleMenu': 'Alternar menú',
  'header.closeMenu': 'Cerrar menú',
  'header.logout': 'Cerrar sesión',

  'layout.quotaBanner': 'Has alcanzado el {percent}% de tu cuota mensual ({used}/{limit}). Considera actualizar tu plan para evitar interrupciones.',

  'playground.title': 'Playground de verificación',
  'playground.step.configure': 'Configurar',
  'playground.step.scanQr': 'Escanear QR',
  'playground.step.results': 'Resultados',
  'playground.liveSignalTag': 'Señal de sandbox en vivo',
  'playground.realWorldMode': 'Modo de simulación real',
  'playground.liveSignal.ready': 'Listo para ejecutar una simulación de verificación realista',
  'playground.liveSignal.pending': 'Esperando escaneo del wallet y callback',
  'playground.liveSignal.inProgress': 'Pruebas recibidas, comprobaciones de política en curso',
  'playground.liveSignal.completed': 'Verificación aceptada y firmada',
  'playground.liveSignal.failed': 'Verificación rechazada por reglas de política',
  'playground.liveSignal.expired': 'La sesión caducó antes de la entrega final de pruebas',
  'playground.liveSignal.description': 'Simula la misma secuencia que en producción: transferencia al wallet, validación de credenciales y decisión de políticas.',
  'playground.executionComplete': '{value}% de ejecución completada',
  'playground.trustSignal': 'Señal de confianza',
  'playground.confidenceScore': 'Puntuación de confianza',
  'playground.highConfidence': 'Confianza alta',
  'playground.strongConfidence': 'Confianza sólida',
  'playground.moderateConfidence': 'Confianza moderada',
  'playground.lowConfidence': 'Confianza baja',
  'playground.passRate': 'Tasa de aprobación',
  'playground.riskScore': 'Puntuación de riesgo',
  'playground.businessUnlock': 'Los escenarios avanzados y perfiles por país se desbloquean en planes superiores.',
  'playground.trial3dHint': 'La demo 3D interactiva está disponible en los planes Pro y Business.',
  'playground.pro3dHint': 'Pro incluye interacción 3D. Business añade animación cinematográfica y perfiles por país.',
  'playground.business3dHint': 'El modo 3D cinematográfico está activo para este sandbox.',
  'playground.threeDProPlus': '3D en Pro+',

  'playground.milestone.session': 'Sesión emitida',
  'playground.milestone.sessionDetail': 'Challenge generado y firmado por WalletGate',
  'playground.milestone.handoff': 'Transferencia al wallet',
  'playground.milestone.handoffPending': 'Esperando respuesta del wallet ({timeLeft} restantes)',
  'playground.milestone.handoffDone': 'Callback del wallet recibido',
  'playground.milestone.proof': 'Verificación de credenciales',
  'playground.milestone.proofDetail': '{count} comprobaciones de política evaluadas',
  'playground.milestone.policy': 'Decisión de política',
  'playground.milestone.policyPending': 'El motor de riesgo prepara la decisión',
  'playground.milestone.policyDone': 'Estado final: {status}',

  'playground.configuration': 'Configuración',
  'playground.environment': 'Entorno',
  'playground.test': 'Prueba',
  'playground.live': 'Live',
  'playground.liveQuotaHint': 'Las verificaciones Live cuentan para tu cuota mensual.',
  'playground.testModeHint': 'Modo test: verificación sandbox segura',
  'playground.liveModeHint': 'Modo live: las verificaciones cuentan en la cuota',
  'playground.apiKey': 'Clave API',
  'playground.createKeyCta': 'Crea una clave de {environment} para continuar →',
  'playground.unnamedKey': 'Clave sin nombre',
  'playground.verificationChecks': 'Comprobaciones de verificación',
  'playground.addCheck': '+ Añadir comprobación',
  'playground.check.ageOver': 'Edad mínima',
  'playground.check.euResidency': 'Residencia en la UE',
  'playground.check.identityVerified': 'Identidad verificada',
  'playground.startVerification': 'Iniciar verificación',
  'playground.creating': 'Creando...',
  'playground.newSession': 'Nueva sesión',
  'playground.createSessionFailed': 'No se pudo crear la sesión',

  'playground.readyToTest': 'Listo para probar',
  'playground.readyToTestHint': 'Configura comprobaciones e inicia una sesión de verificación',
  'playground.waitingForQr': 'Esperando código QR...',

  'playground.verificationSession': 'Sesión de verificación',
  'playground.status.pending': 'pendiente',
  'playground.status.inProgress': 'en progreso',
  'playground.status.completed': 'completado',
  'playground.status.failed': 'fallido',
  'playground.status.expired': 'caducado',
  'playground.scanToVerify': 'Escanear para verificar',
  'playground.scanWithWallet': 'Escanea con la app Wallet EUDI',
  'playground.expiresIn': 'Caduca en {time}',
  'playground.sessionId': 'ID de sesión',
  'playground.deepLink': 'Enlace profundo',
  'playground.verified': 'Verificado',
  'playground.failed': 'Fallido',
  'playground.expired': 'Caducado',
  'playground.checkResults': 'Resultados de comprobación',
  'playground.passed': 'Aprobado',

  'playground.quickSimulate': 'Simulación rápida',
  'playground.testOnly': 'Solo prueba',
  'playground.simulateHint': 'Omite el escaneo del wallet y simula una respuesta al instante.',
  'playground.sim.passAll': 'Aprobar todas las comprobaciones',
  'playground.sim.failAll': 'Fallar todas las comprobaciones',
  'playground.sim.custom': 'Resultados personalizados',
  'playground.sim.scenario': 'Escenarios predefinidos',
  'playground.sim.scenarioLabel': 'Escenario',
  'playground.sim.loadingScenarios': 'Cargando escenarios...',
  'playground.sim.prebuiltRequiresPro': 'Los escenarios predefinidos están disponibles desde Pro.',
  'playground.sim.countryRequiresBusiness': 'Los escenarios de perfil por país requieren el plan Business.',
  'playground.sim.customRequiresPaid': 'Los resultados personalizados requieren un plan de pago.',
  'playground.submitSimulation': 'Enviar simulación',
  'playground.simulating': 'Simulando...',
  'playground.simulationFailed': 'La simulación falló',
  'playground.proLimitReached': 'Se alcanzó el límite Pro de sandbox en los últimos 30 días. Actualiza a Business para ejecuciones ilimitadas.',
  'playground.upgrade': 'Actualizar',

  'playground.scenarioBuilder': 'Constructor de escenarios',
  'playground.businessPlan': 'Plan Business',
  'playground.scenarioBuilderHint': 'Crea recetas de simulación reutilizables para distintos resultados de wallet.',
  'playground.customScenarioBusinessOnly': 'Los escenarios personalizados están disponibles en el plan Business.',
  'playground.complianceReport': 'Informe de cumplimiento',
  'playground.complianceReportHint': 'Exporta un informe de preparación para auditorías y stakeholders.',
  'playground.complianceRequiresBusiness': 'Los informes de cumplimiento requieren el plan Business.',
  'playground.last7Days': 'Últimos 7 días',
  'playground.last30Days': 'Últimos 30 días',
  'playground.last90Days': 'Últimos 90 días',
  'playground.runHistory': 'Historial de ejecuciones',
  'playground.noRuns': 'Aún no hay ejecuciones sandbox. Simula una sesión para ver el historial aquí.',
  'playground.rawJson': 'JSON sin procesar',
  'playground.request': 'Solicitud',
  'playground.response': 'Respuesta',
  'playground.integrationCode': 'Código de integración',
};

const it: TranslationDict = {
  'common.language': 'Lingua',
  'common.dashboard': 'Dashboard',
  'common.apiKeys': 'Chiavi API',
  'common.playground': 'Playground',
  'common.team': 'Team',
  'common.auditLog': 'Registro audit',
  'common.webhooks': 'Webhook',
  'common.security': 'Sicurezza',
  'common.docs': 'Documentazione',
  'common.billing': 'Fatturazione',
  'common.upgrade': 'Aggiorna',
  'common.signOut': 'Esci',
  'common.cancel': 'Annulla',
  'common.downloadHtml': 'Scarica HTML',
  'common.downloadCsv': 'Scarica CSV',
  'common.viewPlans': 'Vedi piani',
  'common.copy': 'Copia',
  'common.copyUrl': 'Copia URL',
  'common.copyResponse': 'Copia risposta',
  'common.copied': 'Copiato!',

  'header.envLocal': 'Locale',
  'header.envStaging': 'Staging',
  'header.envProduction': 'Produzione',
  'header.staff': 'Staff',
  'header.adminTools': 'Strumenti admin',
  'header.superAdmin': 'Super Admin',
  'header.blogManager': 'Gestore blog',
  'header.ownerTools': 'Strumenti proprietario',
  'header.notifications': 'Notifiche',
  'header.signedInAs': 'Accesso effettuato come',
  'header.toggleMenu': 'Apri menu',
  'header.closeMenu': 'Chiudi menu',
  'header.logout': 'Disconnetti',

  'layout.quotaBanner': 'Hai raggiunto il {percent}% della quota mensile ({used}/{limit}). Valuta un upgrade per evitare interruzioni.',

  'playground.title': 'Playground di verifica',
  'playground.step.configure': 'Configura',
  'playground.step.scanQr': 'Scansiona QR',
  'playground.step.results': 'Risultati',
  'playground.liveSignalTag': 'Segnale Sandbox live',
  'playground.realWorldMode': 'Modalità simulazione reale',
  'playground.liveSignal.ready': 'Pronto per una simulazione di verifica realistica',
  'playground.liveSignal.pending': 'In attesa della scansione wallet e del callback',
  'playground.liveSignal.inProgress': 'Prove ricevute, controlli policy in corso',
  'playground.liveSignal.completed': 'Verifica accettata e firmata',
  'playground.liveSignal.failed': 'Verifica rifiutata dalle policy',
  'playground.liveSignal.expired': 'Sessione scaduta prima dell’invio finale delle prove',
  'playground.liveSignal.description': 'Simula la stessa sequenza usata in produzione: passaggio wallet, verifica credenziali e decisione delle policy.',
  'playground.executionComplete': '{value}% esecuzione completata',
  'playground.trustSignal': 'Segnale di affidabilità',
  'playground.confidenceScore': 'Punteggio di fiducia',
  'playground.highConfidence': 'Alta fiducia',
  'playground.strongConfidence': 'Fiducia solida',
  'playground.moderateConfidence': 'Fiducia moderata',
  'playground.lowConfidence': 'Fiducia bassa',
  'playground.passRate': 'Tasso di superamento',
  'playground.riskScore': 'Punteggio di rischio',
  'playground.businessUnlock': 'Scenari avanzati e profili paese sono disponibili nei piani superiori.',
  'playground.trial3dHint': 'La demo 3D interattiva è disponibile nei piani Pro e Business.',
  'playground.pro3dHint': 'Pro include interazione 3D. Business aggiunge animazioni cinematografiche e profili paese.',
  'playground.business3dHint': 'La modalità 3D cinematografica è attiva per questa sandbox.',
  'playground.threeDProPlus': '3D su Pro+',

  'playground.milestone.session': 'Sessione emessa',
  'playground.milestone.sessionDetail': 'Challenge generata e firmata da WalletGate',
  'playground.milestone.handoff': 'Passaggio wallet',
  'playground.milestone.handoffPending': 'In attesa della risposta wallet ({timeLeft} rimanenti)',
  'playground.milestone.handoffDone': 'Callback wallet acquisito',
  'playground.milestone.proof': 'Verifica credenziali',
  'playground.milestone.proofDetail': '{count} controlli policy valutati',
  'playground.milestone.policy': 'Decisione policy',
  'playground.milestone.policyPending': 'Il motore di rischio sta preparando la decisione',
  'playground.milestone.policyDone': 'Stato finale: {status}',

  'playground.configuration': 'Configurazione',
  'playground.environment': 'Ambiente',
  'playground.test': 'Test',
  'playground.live': 'Live',
  'playground.liveQuotaHint': 'Le verifiche Live vengono conteggiate nella quota mensile.',
  'playground.testModeHint': 'Modalità test: verifica sandbox sicura',
  'playground.liveModeHint': 'Modalità live: le verifiche contano nella quota',
  'playground.apiKey': 'Chiave API',
  'playground.createKeyCta': 'Crea una chiave {environment} per continuare →',
  'playground.unnamedKey': 'Chiave senza nome',
  'playground.verificationChecks': 'Controlli di verifica',
  'playground.addCheck': '+ Aggiungi controllo',
  'playground.check.ageOver': 'Età minima',
  'playground.check.euResidency': 'Residenza UE',
  'playground.check.identityVerified': 'Identità verificata',
  'playground.startVerification': 'Avvia verifica',
  'playground.creating': 'Creazione...',
  'playground.newSession': 'Nuova sessione',
  'playground.createSessionFailed': 'Creazione sessione non riuscita',

  'playground.readyToTest': 'Pronto al test',
  'playground.readyToTestHint': 'Configura i controlli e avvia una sessione di verifica',
  'playground.waitingForQr': 'In attesa del codice QR...',

  'playground.verificationSession': 'Sessione di verifica',
  'playground.status.pending': 'in attesa',
  'playground.status.inProgress': 'in corso',
  'playground.status.completed': 'completato',
  'playground.status.failed': 'fallito',
  'playground.status.expired': 'scaduto',
  'playground.scanToVerify': 'Scansiona per verificare',
  'playground.scanWithWallet': 'Scansiona con l’app Wallet EUDI',
  'playground.expiresIn': 'Scade tra {time}',
  'playground.sessionId': 'ID sessione',
  'playground.deepLink': 'Deep link',
  'playground.verified': 'Verificato',
  'playground.failed': 'Non riuscito',
  'playground.expired': 'Scaduto',
  'playground.checkResults': 'Risultati controlli',
  'playground.passed': 'Superato',

  'playground.quickSimulate': 'Simulazione rapida',
  'playground.testOnly': 'Solo test',
  'playground.simulateHint': 'Salta la scansione wallet e simula subito una risposta.',
  'playground.sim.passAll': 'Supera tutti i controlli',
  'playground.sim.failAll': 'Fallisci tutti i controlli',
  'playground.sim.custom': 'Risultati personalizzati',
  'playground.sim.scenario': 'Scenari predefiniti',
  'playground.sim.scenarioLabel': 'Scenario',
  'playground.sim.loadingScenarios': 'Caricamento scenari...',
  'playground.sim.prebuiltRequiresPro': 'Gli scenari di test predefiniti sono disponibili da Pro in su.',
  'playground.sim.countryRequiresBusiness': 'Gli scenari con profili paese richiedono il piano Business.',
  'playground.sim.customRequiresPaid': 'I risultati personalizzati richiedono un piano a pagamento.',
  'playground.submitSimulation': 'Invia simulazione',
  'playground.simulating': 'Simulazione in corso...',
  'playground.simulationFailed': 'Simulazione non riuscita',
  'playground.proLimitReached': 'Limite sandbox Pro degli ultimi 30 giorni raggiunto. Passa a Business per esecuzioni illimitate.',
  'playground.upgrade': 'Aggiorna',

  'playground.scenarioBuilder': 'Costruttore scenari',
  'playground.businessPlan': 'Piano Business',
  'playground.scenarioBuilderHint': 'Crea ricette di simulazione riutilizzabili per diversi esiti wallet.',
  'playground.customScenarioBusinessOnly': 'Gli scenari personalizzati sono disponibili nel piano Business.',
  'playground.complianceReport': 'Report di conformità',
  'playground.complianceReportHint': 'Esporta un report di preparazione per audit e stakeholder.',
  'playground.complianceRequiresBusiness': 'I report di conformità richiedono il piano Business.',
  'playground.last7Days': 'Ultimi 7 giorni',
  'playground.last30Days': 'Ultimi 30 giorni',
  'playground.last90Days': 'Ultimi 90 giorni',
  'playground.runHistory': 'Storico esecuzioni',
  'playground.noRuns': 'Nessuna esecuzione sandbox. Simula una sessione per visualizzare lo storico.',
  'playground.rawJson': 'JSON grezzo',
  'playground.request': 'Richiesta',
  'playground.response': 'Risposta',
  'playground.integrationCode': 'Codice di integrazione',
};

const nl: TranslationDict = {
  'common.language': 'Taal',
  'common.dashboard': 'Dashboard',
  'common.apiKeys': 'API-sleutels',
  'common.playground': 'Playground',
  'common.team': 'Team',
  'common.auditLog': 'Auditlog',
  'common.webhooks': 'Webhooks',
  'common.security': 'Beveiliging',
  'common.docs': 'Documentatie',
  'common.billing': 'Facturatie',
  'common.upgrade': 'Upgraden',
  'common.signOut': 'Uitloggen',
  'common.cancel': 'Annuleren',
  'common.downloadHtml': 'HTML downloaden',
  'common.downloadCsv': 'CSV downloaden',
  'common.viewPlans': 'Abonnementen bekijken',
  'common.copy': 'Kopiëren',
  'common.copyUrl': 'URL kopiëren',
  'common.copyResponse': 'Antwoord kopiëren',
  'common.copied': 'Gekopieerd!',

  'header.envLocal': 'Lokaal',
  'header.envStaging': 'Staging',
  'header.envProduction': 'Productie',
  'header.staff': 'Team',
  'header.adminTools': 'Beheerhulpmiddelen',
  'header.superAdmin': 'Super Admin',
  'header.blogManager': 'Blogbeheer',
  'header.ownerTools': 'Eigenaarstools',
  'header.notifications': 'Meldingen',
  'header.signedInAs': 'Ingelogd als',
  'header.toggleMenu': 'Menu wisselen',
  'header.closeMenu': 'Menu sluiten',
  'header.logout': 'Uitloggen',

  'layout.quotaBanner': 'Je zit op {percent}% van je maandelijkse limiet ({used}/{limit}). Overweeg een upgrade om onderbrekingen te voorkomen.',

  'playground.title': 'Verificatie-playground',
  'playground.step.configure': 'Configureren',
  'playground.step.scanQr': 'QR scannen',
  'playground.step.results': 'Resultaten',
  'playground.liveSignalTag': 'Live Sandbox-signaal',
  'playground.realWorldMode': 'Realistische simulatiemodus',
  'playground.liveSignal.ready': 'Klaar om een realistische verificatiesimulatie te starten',
  'playground.liveSignal.pending': 'Wachten op wallet-scan en callback',
  'playground.liveSignal.inProgress': 'Bewijzen ontvangen, beleidscontroles worden uitgevoerd',
  'playground.liveSignal.completed': 'Verificatie geaccepteerd en ondertekend',
  'playground.liveSignal.failed': 'Verificatie afgewezen door beleidsregels',
  'playground.liveSignal.expired': 'Sessie verlopen vóór definitieve bewijsaanlevering',
  'playground.liveSignal.description': 'Simuleert dezelfde volgorde als in productie: wallet-overdracht, credential-validatie en beleidsbesluit.',
  'playground.executionComplete': '{value}% uitvoering voltooid',
  'playground.trustSignal': 'Vertrouwenssignaal',
  'playground.confidenceScore': 'Vertrouwensscore',
  'playground.highConfidence': 'Hoge zekerheid',
  'playground.strongConfidence': 'Sterke zekerheid',
  'playground.moderateConfidence': 'Gemiddelde zekerheid',
  'playground.lowConfidence': 'Lage zekerheid',
  'playground.passRate': 'Slagingspercentage',
  'playground.riskScore': 'Risicoscore',
  'playground.businessUnlock': 'Uitgebreide scenario’s en landsprofielen zijn beschikbaar in hogere abonnementen.',
  'playground.trial3dHint': 'De interactieve 3D-demo is beschikbaar in Pro- en Business-abonnementen.',
  'playground.pro3dHint': 'Pro bevat 3D-interactie. Business voegt cinematografische animatie en landsprofielen toe.',
  'playground.business3dHint': 'Cinematische 3D-modus is actief voor deze sandbox.',
  'playground.threeDProPlus': '3D in Pro+',

  'playground.milestone.session': 'Sessie uitgegeven',
  'playground.milestone.sessionDetail': 'Challenge gegenereerd en ondertekend door WalletGate',
  'playground.milestone.handoff': 'Wallet-overdracht',
  'playground.milestone.handoffPending': 'Wachten op wallet-reactie ({timeLeft} resterend)',
  'playground.milestone.handoffDone': 'Wallet-callback ontvangen',
  'playground.milestone.proof': 'Credential-validatie',
  'playground.milestone.proofDetail': '{count} beleidscontroles geëvalueerd',
  'playground.milestone.policy': 'Beleidsbesluit',
  'playground.milestone.policyPending': 'Risico-engine bereidt besluit voor',
  'playground.milestone.policyDone': 'Eindstatus: {status}',

  'playground.configuration': 'Configuratie',
  'playground.environment': 'Omgeving',
  'playground.test': 'Test',
  'playground.live': 'Live',
  'playground.liveQuotaHint': 'Live-verificaties tellen mee voor je maandelijkse limiet.',
  'playground.testModeHint': 'Testmodus: veilige sandbox-verificatie',
  'playground.liveModeHint': 'Live-modus: verificaties tellen mee voor de limiet',
  'playground.apiKey': 'API-sleutel',
  'playground.createKeyCta': 'Maak een {environment}-sleutel aan om door te gaan →',
  'playground.unnamedKey': 'Naamloze sleutel',
  'playground.verificationChecks': 'Verificatiecontroles',
  'playground.addCheck': '+ Controle toevoegen',
  'playground.check.ageOver': 'Minimumleeftijd',
  'playground.check.euResidency': 'EU-woonplaats',
  'playground.check.identityVerified': 'Identiteit geverifieerd',
  'playground.startVerification': 'Verificatie starten',
  'playground.creating': 'Aanmaken...',
  'playground.newSession': 'Nieuwe sessie',
  'playground.createSessionFailed': 'Sessie aanmaken mislukt',

  'playground.readyToTest': 'Klaar om te testen',
  'playground.readyToTestHint': 'Configureer controles en start een verificatiesessie',
  'playground.waitingForQr': 'Wachten op QR-code...',

  'playground.verificationSession': 'Verificatiesessie',
  'playground.status.pending': 'in afwachting',
  'playground.status.inProgress': 'bezig',
  'playground.status.completed': 'voltooid',
  'playground.status.failed': 'mislukt',
  'playground.status.expired': 'verlopen',
  'playground.scanToVerify': 'Scan om te verifiëren',
  'playground.scanWithWallet': 'Scan met de EUDI Wallet-app',
  'playground.expiresIn': 'Verloopt over {time}',
  'playground.sessionId': 'Sessie-ID',
  'playground.deepLink': 'Diepe link',
  'playground.verified': 'Geverifieerd',
  'playground.failed': 'Mislukt',
  'playground.expired': 'Verlopen',
  'playground.checkResults': 'Controleresultaten',
  'playground.passed': 'Geslaagd',

  'playground.quickSimulate': 'Snelsimulatie',
  'playground.testOnly': 'Alleen test',
  'playground.simulateHint': 'Sla de wallet-scan over en simuleer direct een respons.',
  'playground.sim.passAll': 'Alle controles laten slagen',
  'playground.sim.failAll': 'Alle controles laten mislukken',
  'playground.sim.custom': 'Aangepaste resultaten',
  'playground.sim.scenario': 'Vooraf ingestelde scenario’s',
  'playground.sim.scenarioLabel': 'Scenario',
  'playground.sim.loadingScenarios': 'Scenario’s laden...',
  'playground.sim.prebuiltRequiresPro': 'Vooraf ingestelde testscenario’s zijn beschikbaar vanaf Pro.',
  'playground.sim.countryRequiresBusiness': 'Landsprofielscenario’s vereisen het Business-abonnement.',
  'playground.sim.customRequiresPaid': 'Aangepaste resultaten vereisen een betaald abonnement.',
  'playground.submitSimulation': 'Simulatie verzenden',
  'playground.simulating': 'Simuleren...',
  'playground.simulationFailed': 'Simulatie mislukt',
  'playground.proLimitReached': 'De Pro-sandboxlimiet voor de afgelopen 30 dagen is bereikt. Upgrade naar Business voor onbeperkte runs.',
  'playground.upgrade': 'Upgraden',

  'playground.scenarioBuilder': 'Scenariobouwer',
  'playground.businessPlan': 'Business-abonnement',
  'playground.scenarioBuilderHint': 'Maak herbruikbare simulatierecepten voor verschillende wallet-uitkomsten.',
  'playground.customScenarioBusinessOnly': 'Aangepaste scenario’s zijn beschikbaar in het Business-abonnement.',
  'playground.complianceReport': 'Compliance-rapport',
  'playground.complianceReportHint': 'Exporteer een gereedheidsrapport voor audits en stakeholders.',
  'playground.complianceRequiresBusiness': 'Compliance-rapporten vereisen het Business-abonnement.',
  'playground.last7Days': 'Afgelopen 7 dagen',
  'playground.last30Days': 'Afgelopen 30 dagen',
  'playground.last90Days': 'Afgelopen 90 dagen',
  'playground.runHistory': 'Uitvoerhistorie',
  'playground.noRuns': 'Nog geen sandbox-runs. Simuleer een sessie om hier historie te zien.',
  'playground.rawJson': 'Ruwe JSON',
  'playground.request': 'Verzoek',
  'playground.response': 'Antwoord',
  'playground.integrationCode': 'Integratiecode',
};

export const translations: Record<Locale, TranslationDict> = {
  en,
  de,
  fr,
  es,
  it,
  nl,
};

export type InterpolationValues = Record<string, string | number>;
